/**
 * Domain Models
 * These types represent the core business entities in the application
 */

/**
 * User Types
 */
export interface UserJson {
  id: number;
  email: string;
  isStaff: boolean;
  isAgent: boolean;
  hasPlanning: boolean;
  lastname: string;
  title: string;
}

/**
 * Client Types
 */
export interface ClientJson {
  display_prices_in_mailing: boolean;
  id: number;
  name: string;
  display_name?: string;
  package_based: boolean;
  invoice_auto: boolean;
  archived: boolean;
  phone1: string | null;
  phone2: string | null;
  intervention_name: string | null;
  intervention_phone: string | null;
  intervention_address: string | null;
  intervention_email: string | null;
  intervention_comment: string | null;
  intervention_days?: string[] | null;
  intervention_hours?: Record<string, { start?: string; end?: string }> | null;
  required_licenses?: string[] | null;
  vehicle_types?: string[] | null;
  vehicle_wash_frequencies?: Record<string, string> | null;
  wash_types?: string[] | null;
  wash_type_other?: string | null;
  wash_frequency_other?: string | null;
  has_additional_vehicles?: boolean;
}

/**
 * Rate Types
 */
export interface RateJson {
  interior: number | null;
  exterior: number | null;
  disinfection: number | null;
  decontamination: number | null;
  package: number | null;
  monthly_subscription: number | null;
  id: number;
  title: string;
  client_id: number;
}

/**
 * Intervention Types
 */
export interface InterventionEditJson {
  interior: boolean;
  exterior: boolean;
  disinfection: boolean;
  decontamination: boolean;
  package: boolean;
  monthly_subscription?: boolean;
  comment: string;
  id?: number;
  driver: string;
  photo_plate: string;
  immatriculation: string;
  parc_number: string;
  immatriculation_bis?: string;
  parc_number_bis?: string;
  timestamp: string;
  prestation_costs?: RateJson;
  author_id?: number;
  client_id?: number;
}

export interface InterventionJson extends InterventionEditJson {
  id: number;
  prestation_costs: RateJson;
  author_id: number;
  client_id: number;
}

export const prestationKeys = ['exterior', 'disinfection', 'interior', 'decontamination', 'package'] as const;
export type PrestationEnum = typeof prestationKeys[number];

export enum UserPresence {
  WORKING = 'working',
  CP = 'cp',
  ABS = 'absence',
}

/**
 * Planning Types
 */
export interface PlanningDayJson {
  id: number;
  presence: UserPresence;
  date: string; // Format: YYYY-MM-DD
  year: number;
  week: number;
  startAt: string | null;
  endAt: string | null;
  comment: string | null;
  dow: number;
  clients: {
    id: number;
    name: string;
    display_name?: string;
    [key: string]: any;
  }[];
  agent: {
    id: number;
    email: string;
    isStaff: boolean;
    hasPlanning: boolean;
    lastname: string;
    title: string;
  };
}

export interface PlanningAgent {
  year: number;
  week: number;
  days: PlanningDayJson[];
  agent: UserJson;
}
